import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { twilioService } from './twilioService';
import { getOrgSmsTemplates, extractVariables } from './orgSmsTemplateService';
import { getFilteredStaff } from './bulkSMSService';

/**
 * Enterprise Bulk SMS Service - Phase 2
 * 
 * Comprehensive SMS system with:
 * - Message queuing and batch processing
 * - Template integration with variable substitution  
 * - Delivery tracking and analytics
 * - Scheduling capabilities
 * - Compliance and retry logic
 * - Real-time progress monitoring
 */

// Configuration
const BULK_SMS_CONFIG = {
  batchSize: 50, // Messages per batch
  delayBetweenBatches: 2000, // 2 seconds between batches
  maxConcurrentBatches: 3,
  maxRetries: 3,
  retryDelay: 5000, // 5 seconds
  maxScheduleDays: 30, // Max days in advance to schedule
  defaultTemplate: {
    emergency: 'URGENT: {message}. Reply SAFE to confirm your status. Sent: {timestamp}',
    operational: '{message}. Please respond if action is required. Sent: {timestamp}',
    general: '{message}. From: {organization_name}. Sent: {timestamp}'
  }
};

// Message status tracking
const MESSAGE_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing', 
  SENT: 'sent',
  DELIVERED: 'delivered',
  FAILED: 'failed',
  RETRYING: 'retrying',
  SCHEDULED: 'scheduled',
  CANCELLED: 'cancelled'
};

// Priority levels
const PRIORITY_LEVELS = {
  EMERGENCY: 1,
  HIGH: 2,
  NORMAL: 3,
  LOW: 4
};

/**
 * Create a new bulk SMS campaign
 */
export const createBulkSMSCampaign = async (campaignData) => {
  try {
    console.log('📱 Creating bulk SMS campaign:', campaignData.name);
    
    const campaign = {
      ...campaignData,
      status: 'draft',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      stats: {
        totalRecipients: 0,
        queued: 0,
        sent: 0,
        delivered: 0,
        failed: 0,
        totalCost: 0
      }
    };
    
    const campaignRef = await addDoc(collection(db, 'c3_bulk_sms_campaigns'), campaign);
    
    console.log('✅ Campaign created with ID:', campaignRef.id);
    return campaignRef.id;
  } catch (error) {
    console.error('❌ Error creating campaign:', error);
    throw error;
  }
};

/**
 * Process message template with variable substitution
 */
export const processMessageTemplate = (template, variables) => {
  if (!template || !variables) return template;
  
  let processedMessage = template;
  
  // Standard variable substitutions
  const standardVariables = {
    timestamp: new Date().toLocaleString(),
    current_time: new Date().toLocaleTimeString(),
    current_date: new Date().toLocaleDateString(),
    ...variables
  };
  
  // Replace all variables in the format {variable_name}
  Object.entries(standardVariables).forEach(([key, value]) => {
    const regex = new RegExp(`{${key}}`, 'g');
    processedMessage = processedMessage.replace(regex, value || '');
  });
  
  return processedMessage;
};

/**
 * Calculate SMS cost estimate
 */
export const calculateSMSCost = (message, recipientCount) => {
  const segmentCount = Math.ceil(message.length / 160);
  const costPerSegment = 0.075; // $0.075 per SMS segment (Twilio pricing)
  return {
    segmentCount,
    totalSegments: segmentCount * recipientCount,
    costPerRecipient: segmentCount * costPerSegment,
    totalCost: segmentCount * recipientCount * costPerSegment,
    currency: 'USD'
  };
};

/**
 * Validate bulk SMS campaign before sending
 */
export const validateBulkSMSCampaign = async (campaignData) => {
  const errors = [];
  const warnings = [];
  
  // Validate basic fields
  if (!campaignData.name?.trim()) {
    errors.push('Campaign name is required');
  }
  
  if (!campaignData.message?.trim()) {
    errors.push('Message content is required');
  }
  
  if (!campaignData.orgId) {
    errors.push('Organization ID is required');
  }
  
  if (!campaignData.filters || Object.keys(campaignData.filters).length === 0) {
    warnings.push('No filters applied - will send to all staff with mobile numbers');
  }
  
  // Validate message length
  if (campaignData.message && campaignData.message.length > 1600) {
    errors.push('Message too long (max 1600 characters for SMS concatenation)');
  }
  
  if (campaignData.message && campaignData.message.length > 160) {
    const segments = Math.ceil(campaignData.message.length / 160);
    warnings.push(`Message will be sent as ${segments} SMS segments (higher cost)`);
  }
  
  // Validate scheduling
  if (campaignData.scheduledFor) {
    const scheduledDate = new Date(campaignData.scheduledFor);
    const now = new Date();
    const maxDate = new Date(now.getTime() + (BULK_SMS_CONFIG.maxScheduleDays * 24 * 60 * 60 * 1000));
    
    if (scheduledDate <= now) {
      errors.push('Scheduled time must be in the future');
    }
    
    if (scheduledDate > maxDate) {
      errors.push(`Cannot schedule more than ${BULK_SMS_CONFIG.maxScheduleDays} days in advance`);
    }
  }
  
  // Validate template variables
  if (campaignData.templateId) {
    try {
      const templates = await getOrgSmsTemplates(db, campaignData.orgId);
      const allTemplates = [...templates.emergency, ...templates.operational, ...templates.general];
      const template = allTemplates.find(t => t.id === campaignData.templateId);
      
      if (!template) {
        errors.push('Selected template not found');
      } else {
        const requiredVariables = extractVariables(template.message);
        const providedVariables = Object.keys(campaignData.templateVariables || {});
        const missingVariables = requiredVariables.filter(v => !providedVariables.includes(v));
        
        if (missingVariables.length > 0) {
          warnings.push(`Missing template variables: ${missingVariables.join(', ')}`);
        }
      }
    } catch (error) {
      warnings.push('Could not validate template variables');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Queue bulk SMS campaign for processing
 */
export const queueBulkSMSCampaign = async (campaignId, filters, message, options = {}) => {
  try {
    console.log('📤 Queuing bulk SMS campaign:', campaignId);
    
    // Get filtered staff
    const staffResults = await getFilteredStaff(options.orgId, filters);
    
    if (staffResults.totalCount === 0) {
      throw new Error('No staff members match the selected filters');
    }
    
    // Create campaign batch
    const batch = writeBatch(db);
    
    // Update campaign with recipient count
    const campaignRef = doc(db, 'c3_bulk_sms_campaigns', campaignId);
    batch.update(campaignRef, {
      status: options.scheduledFor ? MESSAGE_STATUS.SCHEDULED : MESSAGE_STATUS.QUEUED,
      'stats.totalRecipients': staffResults.totalCount,
      'stats.queued': staffResults.totalCount,
      queuedAt: serverTimestamp(),
      scheduledFor: options.scheduledFor || null,
      priority: options.priority || PRIORITY_LEVELS.NORMAL
    });
    
    // Create message queue entries for each recipient
    const messageQueue = staffResults.staff.map((staff, index) => ({
      campaignId,
      recipientId: staff.id,
      recipientName: `${staff.firstName} ${staff.lastName}`,
      recipientPhone: staff.mobile,
      recipientEmail: staff.email,
      recipientSite: staff.siteName,
      message: processMessageTemplate(message, {
        first_name: staff.firstName,
        last_name: staff.lastName,
        full_name: `${staff.firstName} ${staff.lastName}`,
        site_name: staff.siteName,
        organization_name: options.organizationName || 'Crisis Compass',
        role: staff.role,
        department: staff.department || 'N/A'
      }),
      status: MESSAGE_STATUS.QUEUED,
      priority: options.priority || PRIORITY_LEVELS.NORMAL,
      batchNumber: Math.floor(index / BULK_SMS_CONFIG.batchSize),
      createdAt: serverTimestamp(),
      scheduledFor: options.scheduledFor || null,
      retryCount: 0,
      metadata: {
        orgId: options.orgId,
        incidentId: options.incidentId || null,
        userInitiated: options.userInitiated || null,
        filters: filters
      }
    }));
    
    // Add messages to queue in batches (Firestore batch limit is 500)
    const batchSize = 400; // Safe batch size
    for (let i = 0; i < messageQueue.length; i += batchSize) {
      const batchMessages = messageQueue.slice(i, i + batchSize);
      const batch = writeBatch(db);
      
      batchMessages.forEach(message => {
        const messageRef = doc(collection(db, 'c3_bulk_sms_queue'));
        batch.set(messageRef, message);
      });
      
      await batch.commit();
    }
    
    await batch.commit();
    
    console.log(`✅ Queued ${staffResults.totalCount} messages for campaign ${campaignId}`);
    
    // Start processing if not scheduled
    if (!options.scheduledFor) {
      processBulkSMSQueue(campaignId);
    }
    
    return {
      success: true,
      queuedCount: staffResults.totalCount,
      estimatedCost: calculateSMSCost(message, staffResults.totalCount)
    };
    
  } catch (error) {
    console.error('❌ Error queuing bulk SMS campaign:', error);
    throw error;
  }
};

/**
 * Process bulk SMS queue
 */
export const processBulkSMSQueue = async (campaignId) => {
  try {
    console.log('🔄 Processing bulk SMS queue for campaign:', campaignId);
    
    // Update campaign status
    await updateDoc(doc(db, 'c3_bulk_sms_campaigns', campaignId), {
      status: MESSAGE_STATUS.PROCESSING,
      processingStartedAt: serverTimestamp()
    });
    
    // Get queued messages for this campaign
    const messagesQuery = query(
      collection(db, 'c3_bulk_sms_queue'),
      where('campaignId', '==', campaignId),
      where('status', '==', MESSAGE_STATUS.QUEUED),
      orderBy('priority'),
      orderBy('batchNumber')
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    const messages = messagesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    if (messages.length === 0) {
      console.log('✅ No messages to process for campaign:', campaignId);
      return;
    }
    
    // Group messages by batch
    const batches = {};
    messages.forEach(message => {
      const batchNum = message.batchNumber;
      if (!batches[batchNum]) {
        batches[batchNum] = [];
      }
      batches[batchNum].push(message);
    });
    
    // Process batches with controlled concurrency
    const batchNumbers = Object.keys(batches).sort((a, b) => parseInt(a) - parseInt(b));
    let activeBatches = 0;
    
    for (const batchNum of batchNumbers) {
      while (activeBatches >= BULK_SMS_CONFIG.maxConcurrentBatches) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      activeBatches++;
      processBatch(batches[batchNum], campaignId)
        .finally(() => {
          activeBatches--;
        });
      
      // Delay between batch starts
      if (activeBatches > 0) {
        await new Promise(resolve => setTimeout(resolve, BULK_SMS_CONFIG.delayBetweenBatches));
      }
    }
    
  } catch (error) {
    console.error('❌ Error processing bulk SMS queue:', error);
    
    // Update campaign status to failed
    await updateDoc(doc(db, 'c3_bulk_sms_campaigns', campaignId), {
      status: MESSAGE_STATUS.FAILED,
      error: error.message,
      processingEndedAt: serverTimestamp()
    });
  }
};

/**
 * Process a single batch of messages
 */
const processBatch = async (batchMessages, campaignId) => {
  try {
    console.log(`📤 Processing batch of ${batchMessages.length} messages for campaign ${campaignId}`);
    
    const batch = writeBatch(db);
    let sentCount = 0;
    let failedCount = 0;
    
    // Process each message in the batch
    for (const message of batchMessages) {
      try {
        // Update message status to processing
        const messageRef = doc(db, 'c3_bulk_sms_queue', message.id);
        batch.update(messageRef, {
          status: MESSAGE_STATUS.PROCESSING,
          processingStartedAt: serverTimestamp()
        });
        
        // Send SMS using existing Twilio service
        const twilioResponse = await twilioService.sendSMS(
          message.recipientPhone,
          message.message,
          campaignId,
          message.recipientId
        );
        
        // Update message with success status
        batch.update(messageRef, {
          status: MESSAGE_STATUS.SENT,
          sentAt: serverTimestamp(),
          twilioSid: twilioResponse.sid,
          twilioStatus: twilioResponse.status,
          segmentCount: Math.ceil(message.message.length / 160)
        });
        
        sentCount++;
        
      } catch (error) {
        console.error(`❌ Error sending message to ${message.recipientPhone}:`, error);
        
        const messageRef = doc(db, 'c3_bulk_sms_queue', message.id);
        
        if (message.retryCount < BULK_SMS_CONFIG.maxRetries) {
          // Mark for retry
          batch.update(messageRef, {
            status: MESSAGE_STATUS.RETRYING,
            retryCount: increment(1),
            lastError: error.message,
            nextRetryAt: new Date(Date.now() + BULK_SMS_CONFIG.retryDelay)
          });
        } else {
          // Mark as failed
          batch.update(messageRef, {
            status: MESSAGE_STATUS.FAILED,
            failedAt: serverTimestamp(),
            finalError: error.message
          });
          failedCount++;
        }
      }
    }
    
    // Update campaign statistics
    const campaignRef = doc(db, 'c3_bulk_sms_campaigns', campaignId);
    batch.update(campaignRef, {
      'stats.sent': increment(sentCount),
      'stats.failed': increment(failedCount),
      'stats.queued': increment(-(sentCount + failedCount)),
      lastBatchProcessedAt: serverTimestamp()
    });
    
    await batch.commit();
    
    console.log(`✅ Batch processed: ${sentCount} sent, ${failedCount} failed`);
    
  } catch (error) {
    console.error('❌ Error processing batch:', error);
  }
};

/**
 * Get campaign analytics and statistics
 */
export const getBulkSMSAnalytics = async (campaignId) => {
  try {
    // Get campaign data
    const campaignDoc = await getDoc(doc(db, 'c3_bulk_sms_campaigns', campaignId));
    if (!campaignDoc.exists()) {
      throw new Error('Campaign not found');
    }
    
    const campaign = { id: campaignDoc.id, ...campaignDoc.data() };
    
    // Get detailed message statistics
    const messagesQuery = query(
      collection(db, 'c3_bulk_sms_queue'),
      where('campaignId', '==', campaignId)
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    const messages = messagesSnapshot.docs.map(doc => doc.data());
    
    // Calculate detailed analytics
    const analytics = {
      campaign,
      totalMessages: messages.length,
      statusBreakdown: {
        queued: messages.filter(m => m.status === MESSAGE_STATUS.QUEUED).length,
        processing: messages.filter(m => m.status === MESSAGE_STATUS.PROCESSING).length,
        sent: messages.filter(m => m.status === MESSAGE_STATUS.SENT).length,
        delivered: messages.filter(m => m.status === MESSAGE_STATUS.DELIVERED).length,
        failed: messages.filter(m => m.status === MESSAGE_STATUS.FAILED).length,
        retrying: messages.filter(m => m.status === MESSAGE_STATUS.RETRYING).length
      },
      deliveryRate: 0,
      failureRate: 0,
      averageSegments: 0,
      estimatedCost: 0,
      locationBreakdown: {},
      roleBreakdown: {},
      timeMetrics: {
        queuedAt: campaign.queuedAt,
        processingStartedAt: campaign.processingStartedAt,
        processingEndedAt: campaign.processingEndedAt,
        averageProcessingTime: null
      }
    };
    
    // Calculate rates
    if (analytics.totalMessages > 0) {
      analytics.deliveryRate = (analytics.statusBreakdown.sent / analytics.totalMessages) * 100;
      analytics.failureRate = (analytics.statusBreakdown.failed / analytics.totalMessages) * 100;
      analytics.averageSegments = messages.reduce((sum, m) => sum + (m.segmentCount || 1), 0) / analytics.totalMessages;
      analytics.estimatedCost = analytics.averageSegments * analytics.totalMessages * 0.075;
    }
    
    // Calculate location and role breakdowns
    messages.forEach(message => {
      const site = message.recipientSite || 'Unknown';
      const role = message.metadata?.role || 'Unknown';
      
      analytics.locationBreakdown[site] = (analytics.locationBreakdown[site] || 0) + 1;
      analytics.roleBreakdown[role] = (analytics.roleBreakdown[role] || 0) + 1;
    });
    
    return analytics;
    
  } catch (error) {
    console.error('❌ Error getting bulk SMS analytics:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time campaign updates
 */
export const subscribeToCampaignUpdates = (campaignId, callback) => {
  const campaignRef = doc(db, 'c3_bulk_sms_campaigns', campaignId);
  return onSnapshot(campaignRef, callback);
};

/**
 * Cancel a bulk SMS campaign
 */
export const cancelBulkSMSCampaign = async (campaignId) => {
  try {
    const batch = writeBatch(db);
    
    // Update campaign status
    const campaignRef = doc(db, 'c3_bulk_sms_campaigns', campaignId);
    batch.update(campaignRef, {
      status: MESSAGE_STATUS.CANCELLED,
      cancelledAt: serverTimestamp()
    });
    
    // Cancel all queued messages
    const messagesQuery = query(
      collection(db, 'c3_bulk_sms_queue'),
      where('campaignId', '==', campaignId),
      where('status', 'in', [MESSAGE_STATUS.QUEUED, MESSAGE_STATUS.RETRYING])
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    messagesSnapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: MESSAGE_STATUS.CANCELLED,
        cancelledAt: serverTimestamp()
      });
    });
    
    await batch.commit();
    
    console.log('✅ Campaign cancelled:', campaignId);
    return { success: true };
    
  } catch (error) {
    console.error('❌ Error cancelling campaign:', error);
    throw error;
  }
};

/**
 * Retry failed messages in a campaign
 */
export const retryFailedMessages = async (campaignId) => {
  try {
    // Get failed messages
    const messagesQuery = query(
      collection(db, 'c3_bulk_sms_queue'),
      where('campaignId', '==', campaignId),
      where('status', '==', MESSAGE_STATUS.FAILED)
    );
    
    const messagesSnapshot = await getDocs(messagesQuery);
    const batch = writeBatch(db);
    
    messagesSnapshot.docs.forEach(doc => {
      batch.update(doc.ref, {
        status: MESSAGE_STATUS.QUEUED,
        retryCount: 0,
        lastError: null,
        nextRetryAt: null
      });
    });
    
    await batch.commit();
    
    // Restart processing
    processBulkSMSQueue(campaignId);
    
    console.log(`✅ Requeued ${messagesSnapshot.docs.length} failed messages for retry`);
    return { success: true, requeuedCount: messagesSnapshot.docs.length };
    
  } catch (error) {
    console.error('❌ Error retrying failed messages:', error);
    throw error;
  }
};

/**
 * Get recent SMS campaigns for an organization
 */
export const getRecentCampaigns = async (orgId, limitCount = 20) => {
  try {
    const campaignsQuery = query(
      collection(db, 'c3_bulk_sms_campaigns'),
      where('orgId', '==', orgId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(campaignsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
  } catch (error) {
    console.error('❌ Error getting recent campaigns:', error);
    throw error;
  }
};

// Export service object
const enterpriseBulkSMSService = {
  createBulkSMSCampaign,
  processMessageTemplate,
  calculateSMSCost,
  validateBulkSMSCampaign,
  queueBulkSMSCampaign,
  processBulkSMSQueue,
  getBulkSMSAnalytics,
  subscribeToCampaignUpdates,
  cancelBulkSMSCampaign,
  retryFailedMessages,
  getRecentCampaigns,
  MESSAGE_STATUS,
  PRIORITY_LEVELS,
  BULK_SMS_CONFIG
};

export default enterpriseBulkSMSService;

// Export constants for direct import
export { MESSAGE_STATUS, PRIORITY_LEVELS, BULK_SMS_CONFIG }; 